import { visible } from "../event/visibility-observer";
import { data } from "../state/payload";
import { html, nothing, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";

const PADDING = 50;

// Size factors for the srcset attributes: x1, x2.
const FACTORS = [1, 2];

export const IMAGE_TYPE_HOTEL = "hotel";
export const IMAGE_TYPE_MEDIA = "article";
export const IMAGE_MAX_SIZE = "x";

// Formats an image URL.
/**
 * @param {"hotel"|"article"} type
 * @param {string} filename
 * @param {string} size e.g. 100x100, 200x, x300, or just x
 * @param {Object} [options]
 * @param {boolean} [options.noCrop] whether to ignore cropping
 * @param {string} [options.stageCropping] cropping data use for stage pictures
 * @param {boolean} [options.disableWEBP] whether to disable webp
 * @returns {string}
 */
export const getImageUrl = (type, filename, size, options = {}) => {
	// Always load webp images.
	filename = options.disableWEBP
		? filename
		: filename.replace(/.jpg$/, ".webp");

	return `${data.picture_host}/public/cache/${type}/plain-nowater${options.noCrop ? "-nocrop" : ""}/${
		options.stageCropping ? `${options.stageCropping}/` : ""
	}${size}/${filename}`;
};

// Resizes a image size given a ration. Ex: resize('150x150', 2) => '300x300'.
const resize = (size, ratio) =>
	size
		.split("x")
		.map(n => (n === "" ? "" : String(Number.parseInt(n) * ratio)))
		.join("x");

// Formats an IMGSET attribute with the size factores defined above.
/**
 * @param {"hotel"|"article"} type
 * @param {string} filename
 * @param {string} size e.g. 100x100, 200x, x300, or just x
 * @param {Object} [options]
 * @param {boolean} [options.noCrop] whether to ignore cropping
 * @param {string} [options.stageCropping] cropping data use for stage pictures
 * @param {boolean} [options.disableWEBP] whether to disable webp
 * @returns {string}
 */
export const getSrcSet = (type, filename, size, options = {}) =>
	FACTORS.map(
		factor =>
			`${getImageUrl(type, filename, resize(size, factor), options)}  ${factor}x`,
	).join(", ");

class Picture extends HTMLElement {
	constructor() {
		super();
		this.loaded = false;
		this.registered = false;
	}

	static get observedAttributes() {
		return ["onhold"];
	}

	connectedCallback() {
		if (this.getAttribute("onhold") === null) {
			this.register();
		}
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === "onhold" && newValue === null) {
			this.register();
		}
	}

	// Register this widget by the visibility observer for lazy loading.
	async register() {
		if (this.registered) {
			return;
		}

		this.registered = true;
		if (!this.hasAttribute("force-load")) {
			await visible(this, PADDING, 10);
		}

		// Remove initial content. It'll be included in the template until the image
		// is loaded. This feature is intended for content placeholders, where an
		// empty image can be added to make room and prevent layout shifts.
		this.initialNodes = Array.from(this.childNodes);
		this.initialNodes.forEach(child => {
			this.removeChild(child);
		});

		this.render();
	}

	render() {
		const breakpoints = Object.keys(data.grid);
		const [type, filename, alt, stageCropping] = [
			"type",
			"filename",
			"alt",
			"crop",
		].map(attr => this.getAttribute(attr));
		const showLoader = this.hasAttribute("show-loader");
		const disableWEBP = this.hasAttribute("disable-webp");

		// Find out the smallest resolution for the fallback element.
		const sizes = {};
		if (this.hasAttribute("sizes")) {
			this.getAttribute("sizes")
				.split(",")
				.forEach(sizeDef => {
					const [sizeKey, sizeValue] = sizeDef.split(":");
					sizes[sizeKey] = sizeValue;
				});
		}
		const minSize = breakpoints.reduceRight(
			(acc, size) => (sizes[size] ? sizes[size] : acc),
			null,
		);

		// Render the "source" elements. Note that the order is very important
		// here! The first source that matches will be used, that's why the
		// larger resolutions are listed first.
		const sources = breakpoints
			.reverse()
			.filter(size => sizes[size])
			.map(
				size =>
					html`<source
						media="(min-width: ${data.grid[size][0]}px)"
						srcset="
							${getSrcSet(type, filename, sizes[size], {
								stageCropping,
								disableWEBP,
							})}
						"
					/>`,
			);

		// Finally wrap the sources around a picture element and add the
		// fallback.
		const template = html`${showLoader
				? html`<div
						class="content-placeholder ${classMap({ hidden: this.loaded })}"
					></div>`
				: nothing}${!this.loaded ? this.initialNodes : nothing}<picture>
				${sources}
				<img
					src="${getImageUrl(type, filename, minSize, {
						stageCropping,
						disableWEBP,
					})}"
					alt="${alt}"
					class="${classMap({ loaded: this.loaded })}"
					@load=${this.onLoad.bind(this)}
				/>
			</picture>`;

		render(template, this, { host: this });
	}

	onLoad() {
		this.loaded = true;
		this.render();
	}
}

customElements.define("esc-picture", Picture);
